import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { nocService } from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';

const SuperAdminNOC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [allNOCs, setAllNOCs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('stats'); // stats, allNOCs, bulkApprove
  const [selectedRole, setSelectedRole] = useState('ACCOUNTS_SECTION');
  const [csvFile, setCsvFile] = useState(null);
  const [bulkAction, setBulkAction] = useState('APPROVED');
  const [bulkRemarks, setBulkRemarks] = useState('');
  const [processing, setProcessing] = useState(false);
  const [showAllDepartments, setShowAllDepartments] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedNOC, setSelectedNOC] = useState(null);
  
  // Selection states
  const [selectedNOCs, setSelectedNOCs] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [departmentFilter, setDepartmentFilter] = useState('ALL');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [nocTypeFilter, setNocTypeFilter] = useState('ALL');
  const [semesterFilter, setSemesterFilter] = useState('ALL');
  
  // NOT toggles for exclusion filtering
  const [notStatus, setNotStatus] = useState(false);
  const [notDepartment, setNotDepartment] = useState(false);
  const [notNocType, setNotNocType] = useState(false);
  const [notSemester, setNotSemester] = useState(false);
  
  // Advanced approval filters
  const [accountsStatusFilter, setAccountsStatusFilter] = useState('ALL'); // APPROVED, REJECTED, PENDING, ALL
  const [libraryStatusFilter, setLibraryStatusFilter] = useState('ALL');
  const [hodStatusFilter, setHodStatusFilter] = useState('ALL');
  const [examStatusFilter, setExamStatusFilter] = useState('ALL');
  
  // Date filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Show advanced filters
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  useEffect(() => {
    if (user?.role === 'SUPER_ADMIN') {
      fetchStats();
      fetchAllNOCs();
      
      // Auto-refresh stats every 30 seconds
      const interval = setInterval(() => {
        fetchStats();
        fetchAllNOCs();
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [user]);

  const fetchStats = async () => {
    try {
      const response = await nocService.getSuperAdminStats();
      setStats(response);
    } catch (error) {
      toast.error('Failed to fetch statistics');
    }
  };

  const fetchAllNOCs = async () => {
    try {
      setLoading(true);
      const response = await nocService.getAllNOCsForSuperAdmin();
      setAllNOCs(response.nocRequests);
    } catch (error) {
      toast.error('Failed to fetch NOC requests');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkApprove = async () => {
    if (!csvFile) {
      toast.error('Please upload a CSV file');
      return;
    }

    if (!bulkRemarks.trim()) {
      toast.error('Please provide remarks for bulk action');
      return;
    }

    try {
      setProcessing(true);
      const formData = new FormData();
      formData.append('csvFile', csvFile);
      formData.append('role', selectedRole);
      formData.append('action', bulkAction);
      formData.append('remarks', bulkRemarks);

      await nocService.bulkApproveByUSN(formData);
      toast.success(`Bulk ${bulkAction.toLowerCase()} completed successfully`);
      
      // Refresh data
      fetchStats();
      fetchAllNOCs();
      
      // Reset form
      setCsvFile(null);
      setBulkRemarks('');
      document.getElementById('csvInput').value = '';
    } catch (error) {
      toast.error(error.response?.data?.message || 'Bulk action failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleSingleApproval = async (nocId, approvalRole, action, remarks) => {
    try {
      await nocService.superAdminApproveNOC(nocId, {
        role: approvalRole,
        status: action,
        remarks: remarks || `${action} by Super Admin`
      });
      
      toast.success(`NOC ${action.toLowerCase()} successfully`);
      fetchStats();
      fetchAllNOCs();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Action failed');
    }
  };

  const getFilteredNOCs = () => {
    return allNOCs.filter(noc => {
      // Status filter (with NOT option)
      if (statusFilter !== 'ALL') {
        const matches = noc.overallStatus === statusFilter;
        if (notStatus ? matches : !matches) return false;
      }
      
      // Department filter (with NOT option)
      if (departmentFilter !== 'ALL') {
        const matches = noc.student?.department === departmentFilter;
        if (notDepartment ? matches : !matches) return false;
      }
      
      // Semester filter (with NOT option)
      if (semesterFilter !== 'ALL') {
        const matches = noc.student?.semester === semesterFilter;
        if (notSemester ? matches : !matches) return false;
      }
      
      // NOC Type filter (with NOT option)
      if (nocTypeFilter !== 'ALL') {
        const matches = noc.nocType === nocTypeFilter;
        if (notNocType ? matches : !matches) return false;
      }
      
      // Role filter (checks if role has approved/rejected at least)
      if (roleFilter !== 'ALL') {
        const roleApproval = noc.approvals.find(a => a.approverRole === roleFilter);
        if (!roleApproval || roleApproval.status === 'PENDING') return false;
      }
      
      // Advanced approval status filters
      if (accountsStatusFilter !== 'ALL') {
        const accountsApproval = noc.approvals.find(a => a.approverRole === 'ACCOUNTS_SECTION');
        if (!accountsApproval || accountsApproval.status !== accountsStatusFilter) return false;
      }
      
      if (libraryStatusFilter !== 'ALL') {
        const libraryApproval = noc.approvals.find(a => a.approverRole === 'LIBRARY_STAFF');
        if (!libraryApproval || libraryApproval.status !== libraryStatusFilter) return false;
      }
      
      if (hodStatusFilter !== 'ALL') {
        const hodApproval = noc.approvals.find(a => a.approverRole === 'HOD');
        if (!hodApproval || hodApproval.status !== hodStatusFilter) return false;
      }
      
      if (examStatusFilter !== 'ALL') {
        const examApproval = noc.approvals.find(a => a.approverRole === 'EXAM_SECTION');
        if (!examApproval || examApproval.status !== examStatusFilter) return false;
      }
      
      // Date range filter
      if (dateFrom) {
        const nocDate = new Date(noc.createdAt);
        const fromDate = new Date(dateFrom);
        if (nocDate < fromDate) return false;
      }
      
      if (dateTo) {
        const nocDate = new Date(noc.createdAt);
        const toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999); // Include the entire day
        if (nocDate > toDate) return false;
      }
      
      // Search filter
      if (searchTerm.trim()) {
        const search = searchTerm.toLowerCase();
        const matchesUSN = noc.student?.usn?.toLowerCase().includes(search);
        const matchesName = noc.student?.name?.toLowerCase().includes(search);
        const matchesEmail = noc.student?.collegeEmail?.toLowerCase().includes(search);
        
        if (!matchesUSN && !matchesName && !matchesEmail) return false;
      }
      
      return true;
    });
  };
  
  const clearAllFilters = () => {
    setStatusFilter('ALL');
    setDepartmentFilter('ALL');
    setRoleFilter('ALL');
    setNocTypeFilter('ALL');
    setSemesterFilter('ALL');
    setNotStatus(false);
    setNotDepartment(false);
    setNotNocType(false);
    setNotSemester(false);
    setAccountsStatusFilter('ALL');
    setLibraryStatusFilter('ALL');
    setHodStatusFilter('ALL');
    setExamStatusFilter('ALL');
    setDateFrom('');
    setDateTo('');
    setSearchTerm('');
  };
  
  const hasActiveFilters = () => {
    return statusFilter !== 'ALL' || 
           departmentFilter !== 'ALL' || 
           roleFilter !== 'ALL' ||
           nocTypeFilter !== 'ALL' ||
           semesterFilter !== 'ALL' ||
           accountsStatusFilter !== 'ALL' ||
           libraryStatusFilter !== 'ALL' ||
           hodStatusFilter !== 'ALL' ||
           examStatusFilter !== 'ALL' ||
           dateFrom !== '' ||
           dateTo !== '' ||
           searchTerm !== '';
  };
  
  const handleSelectAll = () => {
    const filtered = getFilteredNOCs();
    if (selectAll) {
      setSelectedNOCs(new Set());
      setSelectAll(false);
    } else {
      const allIds = new Set(filtered.map(noc => noc.id));
      setSelectedNOCs(allIds);
      setSelectAll(true);
    }
  };
  
  const handleSelectNOC = (nocId) => {
    const newSelected = new Set(selectedNOCs);
    if (newSelected.has(nocId)) {
      newSelected.delete(nocId);
    } else {
      newSelected.add(nocId);
    }
    setSelectedNOCs(newSelected);
    setSelectAll(false);
  };
  
  const handleBulkActionOnSelected = async (role, action, remarks) => {
    if (selectedNOCs.size === 0) {
      toast.error('No NOCs selected');
      return;
    }
    
    if (!remarks.trim()) {
      toast.error('Please provide remarks for bulk action');
      return;
    }
    
    try {
      setProcessing(true);
      const loadingToast = toast.info(`Processing ${selectedNOCs.size} NOC(s)...`, { autoClose: false });
      
      // Process all NOCs in parallel for speed
      const promises = Array.from(selectedNOCs).map(nocId => 
        nocService.superAdminApproveNOC(nocId, {
          role,
          status: action,
          remarks
        }).then(() => ({ success: true, nocId }))
          .catch((error) => ({ success: false, nocId, error }))
      );
      
      const results = await Promise.all(promises);
      
      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;
      
      toast.dismiss(loadingToast);
      
      if (failCount === 0) {
        toast.success(`✓ All ${successCount} NOC(s) processed successfully`);
      } else {
        toast.warning(`Completed: ${successCount} success, ${failCount} failed`);
      }
      
      setSelectedNOCs(new Set());
      setSelectAll(false);
      fetchStats();
      fetchAllNOCs();
    } catch (error) {
      toast.error('Bulk action failed');
    } finally {
      setProcessing(false);
    }
  };

  const nocTypeLabels = {
    TRANSFER_CERTIFICATE: 'Transfer Certificate',
    COURSE_COMPLETION: 'Course Completion',
    INTERNSHIP: 'Internship',
    SCHOLARSHIP: 'Scholarship',
    EXAM_HALL_TICKET: 'Exam Hall Ticket',
    LIBRARY_CLEARANCE: 'Library Clearance',
    FEE_CLEARANCE: 'Fee Clearance',
    OTHER: 'Other'
  };

  const getStatusBadge = (status) => {
    const styles = {
      PENDING: 'bg-amber-100 text-amber-800',
      APPROVED: 'bg-emerald-100 text-emerald-800',
      REJECTED: 'bg-red-100 text-red-800'
    };
    return styles[status] || 'bg-slate-100 text-slate-800';
  };

  if (user?.role !== 'SUPER_ADMIN') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-2">Access Denied</h1>
          <p className="text-slate-600">This page is only accessible to Super Admins</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-slate-900">Super Admin NOC Management</h1>
          <p className="text-slate-600 mt-1">Complete NOC system oversight and management</p>
        </div>

        {/* View Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="flex border-b border-slate-200">
            <button
              onClick={() => setActiveView('stats')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeView === 'stats'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              📊 Statistics
            </button>
            <button
              onClick={() => setActiveView('allNOCs')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeView === 'allNOCs'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              📋 All NOCs
            </button>
            <button
              onClick={() => setActiveView('bulkApprove')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeView === 'bulkApprove'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              ⚡ Bulk Actions
            </button>
          </div>
        </div>

        {/* Statistics View */}
        {activeView === 'stats' && stats && (
          <div>
            {/* Overall Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-sm font-medium text-slate-600 mb-2">Total NOCs</h3>
                <p className="text-3xl font-bold text-slate-900">{stats.overall?.total || 0}</p>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-sm font-medium text-slate-600 mb-2">Pending</h3>
                <p className="text-3xl font-bold text-amber-600">{stats.overall?.pending || 0}</p>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-sm font-medium text-slate-600 mb-2">Approved</h3>
                <p className="text-3xl font-bold text-green-600">{stats.overall?.approved || 0}</p>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-sm font-medium text-slate-600 mb-2">Rejected</h3>
                <p className="text-3xl font-bold text-red-600">{stats.overall?.rejected || 0}</p>
              </div>
            </div>

            {/* By Role Stats */}
            <div className="bg-white rounded-lg shadow p-6 mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Statistics by Approval Role</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {stats.byRole && Object.entries(stats.byRole).map(([roleKey, roleData]) => (
                  <div key={roleKey} className="border border-slate-200 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-900 mb-3">
                      {roleKey === 'accounts' ? 'Accounts Section' : 
                       roleKey === 'library' ? 'Library Staff' :
                       roleKey === 'hod' ? 'Head of Department' :
                       roleKey === 'exam' ? 'Exam Section' : roleKey}
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Approved:</span>
                        <span className="font-semibold text-green-600">{roleData.approved}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Rejected:</span>
                        <span className="font-semibold text-red-600">{roleData.rejected}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Pending:</span>
                        <span className="font-semibold text-amber-600">{roleData.pending}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* By Department */}
            {stats.byDepartment && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-slate-900">Statistics by Department (HOD Approvals)</h2>
                  <button
                    onClick={() => setShowAllDepartments(!showAllDepartments)}
                    className="px-4 py-2 text-sm font-medium text-primary border border-primary rounded-lg hover:bg-primary hover:text-white transition-colors"
                  >
                    {showAllDepartments ? '🔍 Hide Empty' : '📊 Show All'}
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {Object.entries(stats.byDepartment)
                    .filter(([dept, deptStats]) => showAllDepartments || deptStats.total > 0)
                    .sort((a, b) => b[1].total - a[1].total)
                    .map(([dept, deptStats]) => (
                    <div key={dept} className="border border-slate-200 rounded-lg p-4">
                      <h4 className="font-semibold text-slate-900 mb-3 text-center">{dept}</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">Total NOCs:</span>
                          <span className="font-semibold text-slate-900">{deptStats.total || 0}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">HOD Approved:</span>
                          <span className="font-semibold text-green-600">{deptStats.hodApproved || 0}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">HOD Rejected:</span>
                          <span className="font-semibold text-red-600">{deptStats.hodRejected || 0}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">HOD Pending:</span>
                          <span className="font-semibold text-amber-600">{deptStats.hodPending || 0}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* All NOCs View */}
        {activeView === 'allNOCs' && (
          <div>
            {/* Search Bar */}
            <div className="bg-white rounded-lg shadow p-4 mb-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Search by USN, Name, or Email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="px-4 py-2 text-slate-600 hover:text-slate-900 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
              {/* Basic Filters Row */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center justify-between">
                    <span>Status</span>
                    {statusFilter !== 'ALL' && (
                      <label className="flex items-center gap-1 text-xs font-normal cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notStatus}
                          onChange={(e) => setNotStatus(e.target.checked)}
                          className="w-3 h-3 text-red-600 border-slate-300 rounded focus:ring-red-500"
                        />
                        <span className="text-red-600">NOT</span>
                      </label>
                    )}
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  >
                    <option value="ALL">All Status</option>
                    <option value="PENDING">Pending</option>
                    <option value="APPROVED">Approved</option>
                    <option value="REJECTED">Rejected</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center justify-between">
                    <span>Department</span>
                    {departmentFilter !== 'ALL' && (
                      <label className="flex items-center gap-1 text-xs font-normal cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notDepartment}
                          onChange={(e) => setNotDepartment(e.target.checked)}
                          className="w-3 h-3 text-red-600 border-slate-300 rounded focus:ring-red-500"
                        />
                        <span className="text-red-600">NOT</span>
                      </label>
                    )}
                  </label>
                  <select
                    value={departmentFilter}
                    onChange={(e) => setDepartmentFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  >
                    <option value="ALL">All Departments</option>
                    <option value="CS">CS</option>
                    <option value="CD">CD</option>
                    <option value="CI">CI</option>
                    <option value="CY">CY</option>
                    <option value="EC">EC</option>
                    <option value="ME">ME</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center justify-between">
                    <span>NOC Type</span>
                    {nocTypeFilter !== 'ALL' && (
                      <label className="flex items-center gap-1 text-xs font-normal cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notNocType}
                          onChange={(e) => setNotNocType(e.target.checked)}
                          className="w-3 h-3 text-red-600 border-slate-300 rounded focus:ring-red-500"
                        />
                        <span className="text-red-600">NOT</span>
                      </label>
                    )}
                  </label>
                  <select
                    value={nocTypeFilter}
                    onChange={(e) => setNocTypeFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  >
                    <option value="ALL">All Types</option>
                    <option value="EXAM_HALL_TICKET">Exam Hall Ticket</option>
                    <option value="TRANSFER_CERTIFICATE">Transfer Certificate</option>
                    <option value="COURSE_COMPLETION">Course Completion</option>
                    <option value="INTERNSHIP">Internship</option>
                    <option value="SCHOLARSHIP">Scholarship</option>
                    <option value="LIBRARY_CLEARANCE">Library Clearance</option>
                    <option value="FEE_CLEARANCE">Fee Clearance</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center justify-between">
                    <span>Semester</span>
                    {semesterFilter !== 'ALL' && (
                      <label className="flex items-center gap-1 text-xs font-normal cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notSemester}
                          onChange={(e) => setNotSemester(e.target.checked)}
                          className="w-3 h-3 text-red-600 border-slate-300 rounded focus:ring-red-500"
                        />
                        <span className="text-red-600">NOT</span>
                      </label>
                    )}
                  </label>
                  <select
                    value={semesterFilter}
                    onChange={(e) => setSemesterFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  >
                    <option value="ALL">All Semesters</option>
                    <option value="SEM_1">Semester 1</option>
                    <option value="SEM_2">Semester 2</option>
                    <option value="SEM_3">Semester 3</option>
                    <option value="SEM_4">Semester 4</option>
                    <option value="SEM_5">Semester 5</option>
                    <option value="SEM_6">Semester 6</option>
                    <option value="SEM_7">Semester 7</option>
                    <option value="SEM_8">Semester 8</option>
                  </select>
                </div>
              </div>
              
              {/* Advanced Filters Toggle */}
              <div className="flex justify-between items-center mb-3">
                <button
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  className="text-sm font-medium text-primary hover:text-primary-dark flex items-center gap-2"
                >
                  {showAdvancedFilters ? '▼' : '▶'} Advanced Filters (Approval Status by Role)
                </button>
                {hasActiveFilters() && (
                  <button
                    onClick={clearAllFilters}
                    className="text-sm font-medium text-red-600 hover:text-red-700"
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
              
              {/* Advanced Approval Filters */}
              {showAdvancedFilters && (
                <div className="border-t border-slate-200 pt-4 mt-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Accounts Section
                      </label>
                      <select
                        value={accountsStatusFilter}
                        onChange={(e) => setAccountsStatusFilter(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                      >
                        <option value="ALL">Any Status</option>
                        <option value="APPROVED">✓ Approved</option>
                        <option value="REJECTED">✗ Rejected</option>
                        <option value="PENDING">⏳ Pending</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Library
                      </label>
                      <select
                        value={libraryStatusFilter}
                        onChange={(e) => setLibraryStatusFilter(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                      >
                        <option value="ALL">Any Status</option>
                        <option value="APPROVED">✓ Approved</option>
                        <option value="REJECTED">✗ Rejected</option>
                        <option value="PENDING">⏳ Pending</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        HOD
                      </label>
                      <select
                        value={hodStatusFilter}
                        onChange={(e) => setHodStatusFilter(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                      >
                        <option value="ALL">Any Status</option>
                        <option value="APPROVED">✓ Approved</option>
                        <option value="REJECTED">✗ Rejected</option>
                        <option value="PENDING">⏳ Pending</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Exam Section
                      </label>
                      <select
                        value={examStatusFilter}
                        onChange={(e) => setExamStatusFilter(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                      >
                        <option value="ALL">Any Status</option>
                        <option value="APPROVED">✓ Approved</option>
                        <option value="REJECTED">✗ Rejected</option>
                        <option value="PENDING">⏳ Pending</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Date Range Filter */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Date From
                      </label>
                      <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Date To
                      </label>
                      <input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Active Filter Count */}
              {hasActiveFilters() && (
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <p className="text-sm text-slate-600">
                    {getFilteredNOCs().length} NOC(s) match current filters
                  </p>
                </div>
              )}
            </div>
            
            {/* Bulk Actions Bar */}
            {selectedNOCs.size > 0 && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="font-medium text-indigo-900">
                      {selectedNOCs.size} NOC(s) selected
                    </span>
                    <button
                      onClick={() => {
                        setSelectedNOCs(new Set());
                        setSelectAll(false);
                      }}
                      className="text-sm text-indigo-600 hover:text-indigo-800"
                    >
                      Clear Selection
                    </button>
                  </div>
                  <BulkActionDropdown 
                    onAction={handleBulkActionOnSelected}
                    disabled={processing}
                  />
                </div>
              </div>
            )}

            {/* NOCs Table */}
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="spinner"></div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            className="w-4 h-4 text-primary border-slate-300 rounded focus:ring-primary"
                          />
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Student</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Approvals</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                      {getFilteredNOCs().map(noc => (
                        <NOCRow
                          key={noc.id}
                          noc={noc}
                          nocTypeLabels={nocTypeLabels}
                          onApprove={handleSingleApproval}
                          onClick={() => setSelectedNOC(noc)}
                          isSelected={selectedNOCs.has(noc.id)}
                          onSelect={() => handleSelectNOC(noc.id)}
                        />
                      ))}
                    </tbody>
                  </table>
                  {getFilteredNOCs().length === 0 && (
                    <div className="text-center py-12 text-slate-500">
                      {hasActiveFilters() 
                        ? 'No NOC requests found matching the filters' 
                        : 'No NOC requests found'}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Bulk Approve View */}
        {activeView === 'bulkApprove' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Bulk Approval by CSV</h2>
            
            <div className="max-w-2xl">
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Select Approval Role
                </label>
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  <option value="ACCOUNTS_SECTION">Accounts Section</option>
                  <option value="LIBRARY_STAFF">Library Staff</option>
                  <option value="HOD">Head of Department</option>
                  <option value="EXAM_SECTION">Exam Section</option>
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Action
                </label>
                <select
                  value={bulkAction}
                  onChange={(e) => setBulkAction(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  <option value="APPROVED">Approve</option>
                  <option value="REJECTED">Reject</option>
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Upload CSV File (USN list)
                </label>
                <input
                  id="csvInput"
                  type="file"
                  accept=".csv"
                  onChange={(e) => setCsvFile(e.target.files[0])}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                />
                <p className="text-xs text-slate-500 mt-2">
                  CSV should contain a column named "USN" with student USNs (one per row)
                </p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Remarks
                </label>
                <textarea
                  value={bulkRemarks}
                  onChange={(e) => setBulkRemarks(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="Enter remarks for this bulk action..."
                />
              </div>

              <button
                onClick={handleBulkApprove}
                disabled={processing || !csvFile}
                className="w-full px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
              >
                {processing ? 'Processing...' : `Bulk ${bulkAction === 'APPROVED' ? 'Approve' : 'Reject'}`}
              </button>

              {/* CSV Format Example */}
              <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-2">CSV Format Example:</h3>
                <pre className="text-sm text-blue-800 bg-white p-3 rounded border border-blue-200">
{`USN
1AJ23CS401
1AJ23CS402
1AJ23CS403`}
                </pre>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* NOC Detail Modal */}
      {selectedNOC && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full my-8">
            <div className="p-8">
              {/* Header */}
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-3xl font-bold text-slate-900 mb-2">
                    {nocTypeLabels[selectedNOC.nocType]}
                  </h2>
                  <p className="text-slate-600">
                    Request ID: {selectedNOC.id.slice(0, 8)}...
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusBadge(selectedNOC.overallStatus)}`}>
                    {selectedNOC.overallStatus}
                  </span>
                  <button
                    onClick={() => setSelectedNOC(null)}
                    className="text-slate-400 hover:text-slate-600 text-3xl font-bold leading-none"
                  >
                    ×
                  </button>
                </div>
              </div>

              {/* Student Information */}
              <div className="mb-6">
                <h3 className="text-xl font-bold text-slate-900 mb-4">Student Information</h3>
                <div className="grid md:grid-cols-2 gap-6 bg-slate-50 rounded-lg p-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Student Name</label>
                    <p className="text-lg text-slate-900">{selectedNOC.student.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">USN</label>
                    <p className="text-lg text-slate-900">{selectedNOC.student.usn}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Department</label>
                    <p className="text-lg text-slate-900">{selectedNOC.student.department}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Semester</label>
                    <p className="text-lg text-slate-900">{selectedNOC.student.semester}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Email</label>
                    <p className="text-slate-900">{selectedNOC.student.collegeEmail}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Submitted On</label>
                    <p className="text-slate-900">
                      {new Date(selectedNOC.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* NOC Details */}
              <div className="mb-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-600 mb-2">Reason</label>
                  <p className="text-slate-900 bg-slate-50 rounded-lg p-4">
                    {selectedNOC.reason}
                  </p>
                </div>
                {selectedNOC.additionalDetails && (
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-2">Additional Details</label>
                    <p className="text-slate-900 bg-slate-50 rounded-lg p-4">
                      {selectedNOC.additionalDetails}
                    </p>
                  </div>
                )}
              </div>

              {/* Approval Timeline */}
              <div className="mb-6">
                <h3 className="text-xl font-bold text-slate-900 mb-4">Approval Status</h3>
                <div className="space-y-4">
                  {(() => {
                    const approvalOrder = ['ACCOUNTS_SECTION', 'LIBRARY_STAFF', 'HOD', 'EXAM_SECTION'];
                    const sortedApprovals = [...selectedNOC.approvals].sort((a, b) => 
                      approvalOrder.indexOf(a.approverRole) - approvalOrder.indexOf(b.approverRole)
                    );
                    return sortedApprovals.map((approval, index) => (
                      <div
                        key={approval.id}
                        className={`border-l-4 pl-6 py-4 ${
                          approval.status === 'APPROVED'
                            ? 'border-emerald-500 bg-emerald-50'
                            : approval.status === 'REJECTED'
                            ? 'border-red-500 bg-red-50'
                            : 'border-slate-300 bg-slate-50'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-slate-600 mr-1">{index + 1}.</span>
                            <h4 className="font-semibold text-slate-900">
                              {approval.approverRole.replace('_', ' ')}
                            </h4>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(approval.status)}`}>
                            {approval.status}
                          </span>
                        </div>
                        {approval.status !== 'PENDING' && (
                          <>
                            {approval.approver && (
                              <p className="text-sm text-slate-600 mb-1">
                                {approval.status === 'APPROVED' ? 'Approved' : 'Rejected'} by {approval.approver.name}
                              </p>
                            )}
                            <p className="text-sm text-slate-600 mb-2">
                              on {new Date(approval.approvedAt || approval.rejectedAt).toLocaleDateString()}
                            </p>
                            {approval.remarks && (
                              <p className="text-sm text-slate-700 italic">
                                Remarks: {approval.remarks}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    ));
                  })()}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setSelectedNOC(null)}
                  className="px-6 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Bulk Action Dropdown Component
const BulkActionDropdown = ({ onAction, disabled }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState('ACCOUNTS_SECTION');
  const [selectedAction, setSelectedAction] = useState('APPROVED');
  const [remarks, setRemarks] = useState('');

  const handleSubmit = () => {
    if (!remarks.trim()) {
      toast.error('Please provide remarks');
      return;
    }
    onAction(selectedRole, selectedAction, remarks);
    setShowModal(false);
    setRemarks('');
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        disabled={disabled}
        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed font-medium"
      >
        Apply Bulk Action
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Bulk Action on Selected NOCs</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">Role</label>
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              >
                <option value="ACCOUNTS_SECTION">Accounts Section</option>
                <option value="LIBRARY_STAFF">Library</option>
                <option value="HOD">HOD</option>
                <option value="EXAM_SECTION">Exam Section</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">Action</label>
              <select
                value={selectedAction}
                onChange={(e) => setSelectedAction(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              >
                <option value="APPROVED">Approve</option>
                <option value="REJECTED">Reject</option>
                <option value="PENDING">Set to Pending</option>
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-2">Remarks (Required)</label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                placeholder="Enter remarks for this bulk action..."
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowModal(false);
                  setRemarks('');
                }}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// NOC Row Component
const NOCRow = ({ noc, nocTypeLabels, onApprove, onClick, isSelected, onSelect }) => {
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState('ACCOUNTS_SECTION');
  const [action, setAction] = useState('APPROVED');
  const [remarks, setRemarks] = useState('');

  const handleSubmit = () => {
    onApprove(noc.id, selectedRole, action, remarks);
    setShowApprovalModal(false);
    setRemarks('');
  };

  return (
    <>
      <tr className="hover:bg-slate-50 transition-colors">
        <td className="px-4 py-4">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              onSelect();
            }}
            className="w-4 h-4 text-primary border-slate-300 rounded focus:ring-primary"
          />
        </td>
        <td onClick={onClick} className="px-4 py-4 whitespace-nowrap cursor-pointer">
          <div className="text-sm font-medium text-slate-900">{noc.student?.name}</div>
          <div className="text-xs text-slate-500">{noc.student?.usn}</div>
          <div className="text-xs text-slate-500">{noc.student?.department}</div>
        </td>
        <td onClick={onClick} className="px-4 py-4 whitespace-nowrap text-sm text-slate-900 cursor-pointer">
          {nocTypeLabels[noc.nocType]}
        </td>
        <td onClick={onClick} className="px-4 py-4 whitespace-nowrap cursor-pointer">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            noc.overallStatus === 'APPROVED' ? 'bg-green-100 text-green-800' :
            noc.overallStatus === 'REJECTED' ? 'bg-red-100 text-red-800' :
            'bg-amber-100 text-amber-800'
          }`}>
            {noc.overallStatus}
          </span>
        </td>
        <td onClick={onClick} className="px-4 py-4 cursor-pointer">
          <div className="space-y-1">
            {noc.approvals.map(approval => (
              <div key={approval.id} className="text-xs">
                <span className="font-medium">{approval.approverRole.replace('_', ' ')}:</span>{' '}
                <span className={
                  approval.status === 'APPROVED' ? 'text-green-600' :
                  approval.status === 'REJECTED' ? 'text-red-600' :
                  'text-amber-600'
                }>
                  {approval.status}
                </span>
              </div>
            ))}
          </div>
        </td>
        <td className="px-4 py-4 whitespace-nowrap text-sm">
          <button
            onClick={() => setShowApprovalModal(true)}
            className="px-3 py-1 bg-primary text-white rounded hover:bg-primary-dark transition-colors text-xs"
          >
            Modify
          </button>
        </td>
      </tr>

      {/* Approval Modal */}
      {showApprovalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Modify NOC Approval</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">Role</label>
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              >
                <option value="ACCOUNTS_SECTION">Accounts Section</option>
                <option value="LIBRARY_STAFF">Library</option>
                <option value="HOD">HOD</option>
                <option value="EXAM_SECTION">Exam Section</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">Action</label>
              <select
                value={action}
                onChange={(e) => setAction(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              >
                <option value="APPROVED">Approve</option>
                <option value="REJECTED">Reject</option>
                <option value="PENDING">Set to Pending</option>
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-2">Remarks</label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                placeholder="Optional remarks..."
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowApprovalModal(false)}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SuperAdminNOC;
